import {
  Grid,
  Breadcrumbs,
  Link,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Avatar
} from '@mui/material'
import { useState } from 'react'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { AccountType, type ActiveAccount } from 'core/types'
import { AgencyStatsOptions } from 'Pages/Reports/AgencyStatsOptions'
import { FPLQuintileOptions } from 'Pages/Reports/FPLQuintileOptions'
import { MSDSExportOptions } from 'Pages/Reports/MSDSExportOptions'
import { ProgramExportOptions } from 'Pages/Reports/ProgramExportOptions'
import { DateRangeOptions } from 'Pages/Reports/DateRangeOptions'
import { ChildEnrollmentDetailOptions } from 'Pages/Reports/ChildEnrollmentDetailOptions'
import { ConsecutiveEnrollmentOptions } from 'Pages/Reports/ConsecutiveEnrollmentOptions'
import { GSRPEnrollmentTrendOptions } from 'Pages/Reports/GSRPEnrollmentTrendOptions'
import { GSRPCapacityReport } from 'Pages/Reports/GSRPCapacityReport'
import { PrioritizationReport } from 'Pages/Reports/PrioritizationReport'
import { EnrollmentDetailsReport } from 'Pages/Reports/EnrollmentDetailsReport'
import { useAuth } from 'hooks/use-auth'
import { sendPost } from 'hooks/use-fetch'
import { MiLeapReport } from 'Pages/Reports/MiLeapReport'

// Icon imports
import ListAltIcon from '@mui/icons-material/ListAlt'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import BarChartIcon from '@mui/icons-material/BarChart'
import ChildCareIcon from '@mui/icons-material/ChildCare'
import RepeatIcon from '@mui/icons-material/Repeat'
import ContactPhoneIcon from '@mui/icons-material/ContactPhone'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import GroupsIcon from '@mui/icons-material/Groups'
import InputIcon from '@mui/icons-material/Input'
import AssignmentIcon from '@mui/icons-material/Assignment'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import TableViewIcon from '@mui/icons-material/TableView'
import ClassIcon from '@mui/icons-material/Class'
import { ClassroomRosterReport } from 'Pages/Reports/ClassroomRosterReport'

interface ReportCardProps {
  title: string
  summary: string
  icon: JSX.Element
  onClick: () => void
  color?: 'primary' | 'secondary'
}

function ReportCard ({ title, summary, icon, onClick, color = 'secondary' }: ReportCardProps): JSX.Element {
  const themeColor = color === 'primary' ? '#7136BC' : '#367575'

  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
        <CardContent sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Avatar sx={{ bgcolor: themeColor, mr: 2, flexShrink: 0 }}>
              {icon}
            </Avatar>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', lineHeight: 1.2, wordBreak: 'break-word' }}>
              {title}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {summary}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export function ReportList (): JSX.Element {
  const [caption, setCaption] = useState<string>('Reports')
  const [showList, setShowList] = useState(true)
  const [shownReport, setShownReport] = useState<string | null>(null)
  const accountId: ActiveAccount | undefined = useAccountId()
  const auth = useAuth()
  const isdUser: boolean = (accountId !== undefined && accountId.type === AccountType.ISD && ((auth.user?.securityLevel ?? '') !== 'MiECCAdmin'))
  const serviceProviderUser: boolean = (accountId !== undefined && accountId.type === AccountType.SERVICE_PROVIDER)
  const intakeUser: boolean = (accountId !== undefined && accountId.type === AccountType.INTAKE_USER)
  const mieccAdminUser: boolean = ((auth.user?.securityLevel ?? '') === 'MiECCAdmin')

  const agencyStatsReport = async (): Promise<void> => {
    setShowList(false)
    setShownReport('AgencyStatsOptions')
    setCaption('Agency Stats Report')
  }

  const childEnrollmentDetailReport = async (): Promise<void> => {
    setShowList(false)
    setShownReport('ChildEnrollmentDetailOptions')
    setCaption('Child Enrollment Detail Report')
  }

  const consecutiveEnrollmentReport = async (): Promise<void> => {
    setShowList(false)
    setShownReport('ConsecutiveEnrollmentOptions')
    setCaption('Consecutive Enrollment Report')
  }

  const contactMethodCountReport = async (): Promise<void> => {
    setShowList(false)
    setShownReport('ContactMethodCountOptions')
    setCaption('Contact Method Count Report')
  }

  const fplQuintileReport = async (): Promise<void> => {
    setShowList(false)
    setShownReport('FPLQuintileOptions')
    setCaption('FPL Quintile Report')
  }

  const hmgReceivedSourcesReport = (): void => {
    setShowList(false)
    setShownReport('HMGReceivedSourcesOptions')
    setCaption('HMG Received Sources Report')
  }

  const enrollmentTrendReport = (): void => {
    setShowList(false)
    setShownReport('GSRPEnrollmentTrendOptions')
    setCaption('GSRP Enrollment Trend Report')
  }

  const gsrpCapacityReport = (): void => {
    setShowList(false)
    setShownReport('GSRPCapacityReport')
    setCaption('GSRP Seat Availability')
  }

  const prioritizationReport = (): void => {
    setShowList(false)
    setShownReport('PrioritizationReport')
    setCaption('Prioritization Report')
  }
  const miLeapReport = async (): Promise<void> => {
    setShowList(false)
    setShownReport('MiLeapReport')
    setCaption('MiLeap Report')
  }

  const hmgMiniAssessmentReport = async (): Promise<void> => {
    setShowList(false)
    setShownReport('HMGMiniAssessmentOptions')
    setCaption('HMG Mini Assessment Report')
  }

  const msdsExport = async (): Promise<void> => {
    setShowList(false)
    setShownReport('MSDSExportOptions')
    setCaption('MSDS Export')
  }

  const efBySaReport = async (): Promise<void> => {
    const { response, success } = await sendPost('/Reporting/EligibilityFactorByServiceProviderURL', {})
    if (success) {
      window.open(response, '_blank')?.focus()
    }
  }

  const ucmReport = async (): Promise<void> => {
    const { response, success } = await sendPost('/Reporting/UnassignedCareManagementURL', {})
    if (success) {
      window.open(response, '_blank')?.focus()
    }
  }

  const dubdReport = (): void => {
    setShowList(false)
    setShownReport('DocumentUploadsByDateOptions')
    setCaption('Document Uploads By Date')
  }

  const backToList = (): void => {
    setShownReport(null)
    setCaption('Reports')
    setShowList(true)
  }

  const programExport = (): void => {
    setShowList(false)
    setShownReport('ProgramExportOptions')
    setCaption('Program Export')
  }

  const hmgFidelityReport = (): void => {
    setShowList(false)
    setShownReport('HMGFidelityOptions')
    setCaption('HMG Fidelity Report')
  }

  const enrollmentDetailsReport = (): void => {
    setShowList(false)
    setShownReport('EnrollmentDetailsReport')
    setCaption('Enrollment Details Report')
  }

  const classroomRosterReport = (): void => {
    setShowList(false)
    setShownReport('ClassroomRosterReport')
    setCaption('Classroom Enrollment Report')
  }

  return <>
    <Typography variant="h3" sx={{ mb: 2 }}>{caption}</Typography>
    {!showList && <Breadcrumbs maxItems={1} aria-label="breadcrumb">
      <Link component="button" sx={{ cursor: 'pointer' }} underline="hover" color="inherit" onClick={backToList} >
        Return
      </Link>
    </Breadcrumbs>
    }
    {showList && <Box sx={{ pb: 4 }}>
      {/* v4 reporting section */}
      <Typography variant="h5" sx={{ mt: 4, mb: 3, fontWeight: 'bold', color: '#333', borderBottom: '2px solid #7136BC', display: 'inline-block', pb: 0.5 }}>
        v4 reporting
      </Typography>
      <Grid container spacing={3}>
        {(isdUser || intakeUser) && <>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Prioritization Report"
              summary="Rank and prioritize children in care management/pending release based on eligibility factors and facility preferences."
              icon={<ListAltIcon />}
              onClick={prioritizationReport}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Enrollment Details Report"
              summary="Comprehensive enrollment details including child demographics, eligibility factors, and service information."
              icon={<TableViewIcon />}
              onClick={enrollmentDetailsReport}
              color="primary"
            />
          </Grid>
        </>
        }
        {serviceProviderUser && <Grid item xs={12} sm={6} md={4}>
          <ReportCard
            title="Classroom Enrollment Report"
            summary="View enrolled and active children for a specific classroom and program, including caregiver contact information."
            icon={<ClassIcon />}
            onClick={classroomRosterReport}
            color="primary"
          />
        </Grid>
        }
        {(isdUser || serviceProviderUser) && <Grid item xs={12} sm={6} md={4}>
          <ReportCard
            title="Child Enrollment Detail"
            summary="Detailed breakdown of individual child enrollment statuses and associated data."
            icon={<ChildCareIcon />}
            onClick={enrollmentDetailsReport}
            color="primary"
          />
        </Grid>
        }
        {isdUser && <>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="GSRP Capacity"
              summary="Shows the capacity of the GSRP program throughout your ISD with options to view as a map, table and exportable report."
              icon={<GroupsIcon />}
              onClick={gsrpCapacityReport}
              color="primary"
            />
          </Grid>
        </>
        }
      </Grid>

      {/* Classic Reports section */}
      <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 'bold', color: '#666', borderBottom: '2px solid #367575', display: 'inline-block', pb: 0.5 }}>
        Classic Reports
      </Typography>
      <Grid container spacing={3}>
        {mieccAdminUser && <Grid item xs={12} sm={6} md={4}>
          <ReportCard
            title="GSRP Enrollment Trend"
            summary="Track enrollment numbers and patterns over specific time periods."
            icon={<TrendingUpIcon />}
            onClick={enrollmentTrendReport}
          />
        </Grid>
        }
        {isdUser && <>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Agency Stats"
              summary="High-level statistical overview of agency-specific performance and metrics."
              icon={<BarChartIcon />}
              onClick={agencyStatsReport}
            />
          </Grid>
        </>
        }
        {(isdUser || serviceProviderUser) && <>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Child Enrollment Detail"
              summary="Detailed breakdown of individual child enrollment statuses and associated data."
              icon={<ChildCareIcon />}
              onClick={childEnrollmentDetailReport}
            />
          </Grid>
        </>}
        {isdUser && <>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Consecutive Enrollment"
              summary="Identify and track children with continuous enrollment across multiple periods."
              icon={<RepeatIcon />}
              onClick={consecutiveEnrollmentReport}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Contact Method Count"
              summary="Analyze the frequency and effectiveness of different caregiver outreach methods."
              icon={<ContactPhoneIcon />}
              onClick={contactMethodCountReport}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Document Uploads By Date"
              summary="Monitor the volume and timing of document submissions across the system."
              icon={<CloudUploadIcon />}
              onClick={dubdReport}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Eligibility Factors"
              summary="Review the distribution of qualifying factors across different service providers."
              icon={<FactCheckIcon />}
              onClick={efBySaReport}
            />
          </Grid>
        </>
        }
        {(isdUser || serviceProviderUser) &&
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="FPL Quintile"
              summary="Demographic analysis of families based on Federal Poverty Level quintiles."
              icon={<GroupsIcon />}
              onClick={fplQuintileReport}
            />
          </Grid>
        }
        {isdUser &&
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="HMG Received Sources"
              summary="Track the origins and referral sources for Help Me Grow inquiries."
              icon={<InputIcon />}
              onClick={hmgReceivedSourcesReport}
            />
          </Grid>
        }
        {(isdUser || intakeUser) && <>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="HMG Mini Assessment"
              summary="Summary of results from preliminary Help Me Grow developmental screenings."
              icon={<AssignmentIcon />}
              onClick={hmgMiniAssessmentReport}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="HMG Fidelity"
              summary="Summary of results from Care Management."
              icon={<AssignmentIcon />}
              onClick={hmgFidelityReport}
            />
          </Grid>
        </>
        }
        {isdUser && <>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="Unassigned Care Management"
              summary="List of children requiring care management assignment or follow-up."
              icon={<AssignmentLateIcon />}
              onClick={ucmReport}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ReportCard
              title="MiLeap"
              summary="Michigan's Learning and Educational Advancement for Pre-K enrollment and program data."
              icon={<AssignmentLateIcon />}
              onClick={miLeapReport}
            />
          </Grid>
        </>
        }
        {(isdUser || serviceProviderUser) && <Grid item xs={12} sm={6} md={4}>
          <ReportCard
            title="MSDS Export"
            summary="Generate data files formatted for Michigan Student Data System state reporting."
            icon={<FileDownloadIcon />}
            onClick={msdsExport}
          />
        </Grid>
        }
        {(isdUser || serviceProviderUser || intakeUser) && <Grid item xs={12} sm={6} md={4}>
          <ReportCard
            title="Program Export"
            summary="Comprehensive data export of program-specific information and metrics."
            icon={<FileUploadIcon />}
            onClick={programExport}
          />
        </Grid>
        }

      </Grid>
    </Box >
    }
    {shownReport === 'AgencyStatsOptions' && <AgencyStatsOptions />}
    {shownReport === 'ChildEnrollmentDetailOptions' && <ChildEnrollmentDetailOptions />}
    {shownReport === 'ConsecutiveEnrollmentOptions' && <ConsecutiveEnrollmentOptions />}
    {shownReport === 'ContactMethodCountOptions' && <DateRangeOptions endpoint='ContactMethodCountReportURL' />}
    {shownReport === 'FPLQuintileOptions' && <FPLQuintileOptions isdUser={isdUser} />}
    {shownReport === 'MSDSExportOptions' && <MSDSExportOptions isdUser={isdUser} />}
    {shownReport === 'ProgramExportOptions' && <ProgramExportOptions isdUser={isdUser || intakeUser} />}
    {shownReport === 'HMGMiniAssessmentOptions' && <DateRangeOptions endpoint='HMGMiniAssessmentReportURL' />}
    {shownReport === 'HMGReceivedSourcesOptions' && <DateRangeOptions endpoint='HMGReceivedOptionsReportURL' />}
    {shownReport === 'GSRPEnrollmentTrendOptions' && <GSRPEnrollmentTrendOptions />}
    {shownReport === 'GSRPCapacityReport' && <GSRPCapacityReport />}
    {shownReport === 'PrioritizationReport' && <PrioritizationReport />}
    {shownReport === 'EnrollmentDetailsReport' && <EnrollmentDetailsReport />}
    {shownReport === 'ClassroomRosterReport' && <ClassroomRosterReport />}
    {shownReport === 'DocumentUploadsByDateOptions' && <DateRangeOptions endpoint='DocumentUploadsByDateReportURL' />}
    {shownReport === 'MiLeapReport' && <MiLeapReport />}
    {shownReport === 'HMGFidelityOptions' && <DateRangeOptions endpoint='HMGFidelityReportURL' />}
  </>
}
